import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, from } from 'rxjs';
import { AuthService } from '@core/services/auth/auth.service';
import { map, switchMap } from 'rxjs/operators';
import { Auth, User, authState } from '@angular/fire/auth';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  // Built in the constructor, i.e. in the injection context: AngularFire warns
  // when `authState()` is called later from inside the guard's switchMap.
  private readonly user$: Observable<User | null>;

  constructor(
    private authService: AuthService,
    private router: Router,
    private auth: Auth
  ) {
    this.user$ = authState(this.auth);
  }

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // Wait for Firebase Auth to finish initializing, then check authentication
    return from(this.auth.authStateReady()).pipe(
      switchMap(() => this.user$),
      map(user => {
        if (user) {
          // Ensure the AuthService is in sync
          this.authService.isUserAuthenticated.next(true);
          return true;
        }
        // Not authenticated — try auto-login as a fallback
        return this.tryAutoLoginAndRedirect(_state.url);
      }),
      switchMap(result => {
        if (typeof result === 'boolean') {
          return [result];
        } else {
          // result is a Promise from tryAutoLoginAndRedirect
          return from(result as Promise<boolean | UrlTree>);
        }
      })
    );
  }

  private async tryAutoLoginAndRedirect(attemptedUrl: string): Promise<boolean | UrlTree> {
    try {
      const autoLoginSuccess = await this.authService.tryAutoLogin();
      return autoLoginSuccess ? true : this.loginRedirect(attemptedUrl);
    } catch (error) {
      console.error('🛡️ AuthGuard: Error during auto-login:', error);
      return this.loginRedirect(attemptedUrl);
    }
  }

  /**
   * `/login`, carrying the originally-requested URL so `AuthShellComponent`
   * can send the user there after they sign in — otherwise a WhatsApp deep
   * link (`/?card=<id>`) always dropped the visitor on the generic cockpit
   * inbox instead of the specific card.
   */
  private loginRedirect(attemptedUrl: string): UrlTree {
    return attemptedUrl
      ? this.router.createUrlTree(['/login'], { queryParams: { returnUrl: attemptedUrl } })
      : this.router.createUrlTree(['/login']);
  }
}